import classNames from "classnames";
import { FieldState, FormState } from "formstate";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { L10n } from "@fidget/common/i18n";
import { DAY_NAMES, WorkingInterval } from "@fidget/common/utils/getWorkingHours";
import { useStore } from "../../../models";
import { IWorkingWeek } from "../../../models/currentUser/CurrentUserStore";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { DiscardApplyButtons } from "../discardApplyButtons";
import { WorkingDay } from "@fidget/common/models/workingDay";
import styles from "./workingHoursContainer.css";
import { WorkingHoursRow } from "./workingHoursRow";

interface Props {
  workingHours: IWorkingWeek;
  zoneId: string;
}

type WorkingDayFormType = FormState<{
  from: FieldState<number>;
  to: FieldState<number>;
}>;

export const WorkingHoursContainer = observer(({ workingHours, zoneId }: Props) => {
  const { currentUserStore } = useStore();
  const [editing, setEditing] = useState<boolean>(false);

  const createWorkingDays = () =>
    workingHours.map((interval, i) => {
      return new WorkingDay(DAY_NAMES[i]!, interval);
    });

  const [newWorkingDays, setNewWorkingDays] = useState(createWorkingDays());

  const createForms = () =>
    newWorkingDays.map((day, i) => {
      return day.intervalForm;
    });

  const [forms, setForms] = useState<Array<WorkingDayFormType>>(createForms());

  useEffect(() => {
    setForms(createForms());
  }, [newWorkingDays]);

  const startEditing = () => {
    if (!editing) {
      setEditing(true);
    }
  };

  const changeInterval = (index: number, newInterval: WorkingInterval) => {
    newWorkingDays[index]!.setValues(newInterval);
    if (!newWorkingDays[index]!.isDisabled) {
      newWorkingDays[index]!.setPreviousDayInterval(newInterval);
      while (++index < 7 && newWorkingDays[index]!.isDisabled) {
        newWorkingDays[index]!.setPreviousDayInterval(newInterval);
      }
    }
  };

  const enableAll = () => {
    newWorkingDays
      .filter((day) => day.isDisabled)
      .forEach((day) => day.setValues(day.previousDayInterval));
  };

  const disableAll = () => {
    newWorkingDays.forEach((day) => {
      day.setValues({ from: day.dayStart, to: day.dayStart });
    });
  };

  const onSaveChanges = () => {
    const workingWeek = forms.map((form: WorkingDayFormType) => ({
      from: form.$.from.value,
      to: form.$.to.value
    }));
    currentUserStore.setWorkingHours(workingWeek);
    setEditing(false);
  };

  const onDiscardChanges = () => {
    setEditing(false);
    setNewWorkingDays(createWorkingDays());
  };

  const revertToDefault = () => {
    newWorkingDays.map((day) => {
      day.setValues(day.defaultWorkingHours);
      day.setPreviousDayInterval(day.defaultWorkingHours);
    });
  };

  const render = () => {
    const enabledAll = newWorkingDays.every((day) => !day.isDisabled);
    const disabledAll = newWorkingDays.every((day) => day.isDisabled);
    const revertToDefaultIsShow = newWorkingDays.some((day) => !day.isDefault);
    return (
      <form
        className={classNames(styles["working-hours-container"], { [styles.editing]: editing })}
        onClick={startEditing}
        onSubmit={(ev) => ev.preventDefault()}
      >
        <div className={styles["days-wrapper"]}>
          <div className={styles["tz-info"]}>TZ: {zoneId}</div>
          <div className={styles["buttons-container"]}>
            <AgentButton
              buttonStyle={"transparent secondary"}
              onClick={enabledAll ? disableAll : enableAll}
            >
              <L10n
                id={
                  enabledAll
                    ? "settings.workingHours.disableAll"
                    : "settings.workingHours.enableAll"
                }
              />
            </AgentButton>
            {revertToDefaultIsShow && (
              <AgentButton buttonStyle={"transparent secondary"} onClick={revertToDefault}>
                <L10n id={"settings.workingHours.revertToDefault"} />
              </AgentButton>
            )}
          </div>
          {newWorkingDays.map((day, index) => (
            <WorkingHoursRow
              day={day}
              onChange={(interval) => changeInterval(index, interval)}
              key={day.name}
            />
          ))}
        </div>
        <div className={styles["save-container"]}>
          <DiscardApplyButtons
            onApply={onSaveChanges}
            onDiscard={onDiscardChanges}
            withDescription
            hidden={!editing}
          />
          {disabledAll && (
            <div className={styles.warning}>
              <i className={classNames("icon-exclamation-mark", styles.icon)} />
              <L10n id={"settings.workingHours.warning"} />
            </div>
          )}
        </div>
      </form>
    );
  };

  return render();
});
